import { Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  getLatestValidStepIndex,
  store,
  useAppState,
  useCheckout,
  useConfirmation,
  useQuote,
} from '../../store/store';
import { AppStatus, StoreEvent } from '../../store/store.types';
import { AppRoute } from '../../enums/appRoutes';
import { LoadingPage } from '../../pages/LoadingPage';
import { formStepRoutes } from '../routes';
import { useRef, useEffect } from 'react';
import { ApplianceDetailsSkeleton } from '../../pages/ApplianceDetailsPage/components/ApplianceDetailsSkeleton';

export const AppGuard = () => {
  const app = useAppState();
  const quote = useQuote();
  const checkout = useCheckout();
  const confirmation = useConfirmation();
  const location = useLocation();

  /**
   * Placeholder effect and until we figure out how to properly handle session creation failures. This checks if they
   * are in an error state and if they have been redirected to the error page, if so it creates a new session when they
   * leave. We need this because currently if session creation fails, the app gets stuck in an error state and the user
   * has no way to recover from it.
   */
  const prevPathname = useRef(location.pathname);

  useEffect(() => {
    if (
      app.status === AppStatus.ERROR &&
      prevPathname.current === AppRoute.ERROR &&
      location.pathname !== AppRoute.ERROR
    ) {
      store.send({ type: StoreEvent.CREATE_SESSION });
    }
    prevPathname.current = location.pathname;
  }, [location.pathname]);

  if (app.status === AppStatus.LOADING) {
    return <ApplianceDetailsSkeleton />;
  }

  if (location.pathname === AppRoute.QUOTE && !quote?.data) {
    return <Navigate to={formStepRoutes[getLatestValidStepIndex()].path} replace />;
  }

  if (location.pathname === AppRoute.CHECKOUT) {
    if (checkout?.loading) return <LoadingPage />;
    if (!checkout?.data) {
      if (confirmation?.purchaseData) {
        return <LoadingPage />;
      }
      return quote?.data ? (
        <Navigate to={AppRoute.QUOTE} replace />
      ) : (
        <Navigate to={formStepRoutes[getLatestValidStepIndex()].path} replace />
      );
    }
  }

  if (location.pathname === AppRoute.CONFIRMATION) {
    if (!confirmation?.regOnlyData && !confirmation?.purchaseData) {
      return quote?.data ? (
        <Navigate to={AppRoute.QUOTE} replace />
      ) : (
        <Navigate to={formStepRoutes[getLatestValidStepIndex()].path} replace />
      );
    }
  }

  return <Outlet />;
};
